import { useParams, Link, Navigate } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { parseCombo, SERVICES, CITIES, comboPath } from "@/data/seoPages";
import { IMAGES, NAP } from "@/data/site";
import { CheckCircle, ArrowRight, MapPin } from "@phosphor-icons/react";
import { useSEO, serviceLd, breadcrumbLd, faqLd, localBusinessLd } from "@/lib/seo";

const IMG_BY_GROUP = {
  Construction: IMAGES.portfolio1,
  Trades: IMAGES.buildPanel,
  Commercial: IMAGES.commercial,
  "Digital & AI": IMAGES.growPanel,
};

export default function ComboLanding() {
  const { combo } = useParams();
  const match = parseCombo(combo);
  const svc = match?.service;
  const city = match?.city;
  const isDigital = svc?.group === "Digital & AI";
  const available = isDigital || city?.core;

  const faqs = match
    ? [
        {
          q: `Do you offer ${svc.keyword} in ${city.name}?`,
          a: available
            ? `Yes — MRE provides ${svc.name.toLowerCase()} in ${city.name}, ${city.code}. Book a free consultation and we'll send a transparent quote.`
            : `We offer ${svc.name.toLowerCase()} as a select project in the ${city.province} area, and our digital services are available across Canada. Reach out and we'll let you know what we can take on.`,
        },
        {
          q: `How much does ${svc.name.toLowerCase()} cost in ${city.name}?`,
          a: `Every project is quoted individually. We offer honest, medium-tier pricing — a free consultation and a transparent quote with no surprises.`,
        },
        {
          q: `Why choose MRE for ${svc.keyword} in ${city.name}?`,
          a: `One partner for the structure and the signal: we build the space and the brand. Licensed, insured and tech-forward, with clear process and plain English.`,
        },
      ]
    : [];

  useSEO({
    title: match ? `${svc.name} in ${city.name} | Free Quote — MRE` : "Services | MRE Developments",
    description: match
      ? `${svc.keyword} in ${city.name}, ${city.code}: ${svc.subKeywords.slice(0, 3).join(", ")}. Honest pricing, free quote, no surprises from MRE Developments.`
      : "",
    path: match ? comboPath(svc.slug, city.slug) : "/services",
    image: match ? IMG_BY_GROUP[svc.group] : IMAGES.buildPanel,
    jsonLd: match
      ? [
          serviceLd(`${svc.name} in ${city.name}`, `${svc.intro} Serving ${city.name}, ${city.province}.`),
          localBusinessLd(),
          faqLd(faqs),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: svc.name, path: `/services/${svc.slug}` },
            { name: city.name, path: comboPath(svc.slug, city.slug) },
          ]),
        ]
      : [],
  });

  if (!match) return <Navigate to="/services" replace />;

  const otherCities = CITIES.filter((c) => c.slug !== city.slug && (isDigital || c.core)).slice(0, 8);
  const otherServices = SERVICES.filter((s) => s.group === svc.group && s.slug !== svc.slug).slice(0, 6);

  return (
    <div data-testid="combo-page">
      <PageHero
        overline={`${svc.group} · ${city.province}`}
        title={`${svc.name} in ${city.name}`}
        intro={`${svc.intro} ${available ? `Proudly serving ${city.name}, ${city.code}.` : `Available as a select project in ${city.name} — digital services Canada-wide.`}`}
        image={IMG_BY_GROUP[svc.group]}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: svc.name, path: `/services/${svc.slug}` },
          { name: city.name, path: comboPath(svc.slug, city.slug) },
        ]}
      />

      {/* Keyword strip */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6 flex flex-wrap gap-3" data-testid="combo-keywords">
          {[`${svc.keyword} ${city.name}`, ...svc.subKeywords.map((k) => `${k} ${city.name}`)].map((k) => (
            <span key={k} className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground border border-border px-3 h-8 inline-flex items-center">
              {k}
            </span>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading
            overline={`${svc.name} · ${city.name}`}
            title={`${svc.name} done right in ${city.name}`}
            intro={`Here's what our ${svc.keyword} service covers for ${city.name} ${isDigital ? "businesses" : "homeowners and businesses"}.`}
          />
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-7 h-12 hover:bg-[hsl(16,100%,55%)] transition-colors" data-testid="combo-cta">
            Get a free quote <ArrowRight size={15} />
          </Link>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 border-t border-l border-border">
          {svc.points.map((p, i) => (
            <Reveal key={p} delay={i * 70} className="border-b border-r border-border p-7 hover:bg-secondary/40 transition-colors">
              <CheckCircle size={22} className="text-primary" weight="light" />
              <h3 className="font-display uppercase tracking-tight text-xl mt-3">{p}</h3>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading overline="FAQ" title={`${svc.name} in ${city.name}`} />
          </div>
          <div className="lg:col-span-8 border-t border-border">
            {faqs.map((f, i) => (
              <Reveal key={i} className="border-b border-border py-6">
                <h3 className="font-display uppercase tracking-tight text-lg">{f.q}</h3>
                <p className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{f.a}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Same service, other cities */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <SectionHeading overline="Also available in" title={`${svc.name} near ${city.name}`} />
        <div className="flex flex-wrap gap-3 mt-8">
          {otherCities.map((c) => (
            <Link key={c.slug} to={comboPath(svc.slug, c.slug)} data-testid={`combo-city-${c.slug}`} className="font-mono-tech text-xs tracking-widest uppercase border border-border px-4 h-10 inline-flex items-center gap-2 hover:border-primary hover:text-primary transition-colors">
              <MapPin size={13} /> {svc.name} in {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Other services in this city */}
      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
          <SectionHeading overline={`More in ${city.name}`} title={`Other ${svc.group.toLowerCase()} services`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mt-10">
            {otherServices.map((s) => (
              <Link key={s.slug} to={comboPath(s.slug, city.slug)} data-testid={`combo-service-${s.slug}`} className="bg-background p-6 hover:bg-secondary/40 transition-colors group">
                <span className="font-mono-tech text-[10px] tracking-widest uppercase text-primary">{s.group}</span>
                <h3 className="font-display uppercase tracking-tight text-lg mt-1 group-hover:text-primary transition-colors">{s.name} in {city.name}</h3>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={`/services/${svc.slug}`} className="font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 inline-flex items-center gap-2 transition-all">
              All {svc.name} <ArrowRight size={14} />
            </Link>
            <Link to={`/locations/${city.slug}`} className="font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 inline-flex items-center gap-2 transition-all">
              Everything in {city.name} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection title={`${svc.name} in ${city.name}?`} subtitle={`Free consultation and a transparent quote — no jargon, no surprises.`} />
    </div>
  );
}
